import { useEffect, useMemo, useState } from "react";
import api, { fmtErr } from "@/shared/lib/api";
import { useAuth } from "@/shared/lib/auth";
import { fmtIST } from "@/shared/lib/time";
import { PageHeader, Btn, Input, Field } from "@/shared/components/UI";
import {
  Plus, Trash2, X, AlertTriangle, KeyRound, Pencil, ShieldCheck,
  Search, Copy, EyeOff, Eye as EyeIcon,
} from "lucide-react";
import { toast } from "sonner";

/* Phase 35 (Feb 2026) — Admin user management with role + password editing.
   Single page, two surfaces:
     • Top: list with quick role pill, last-login (or created), inline actions.
     • Slide-over: edit role / reset password / delete, with friendly copy &
       a "generate strong password" button.
   No new backend route needed — uses the existing PATCH /api/users/{uid}. */

const ROLE_META = {
  admin:       { label: "Admin",         tint: "bg-zinc-900 text-white",            desc: "Full access to everything" },
  management:  { label: "Management",    tint: "bg-amber-100 text-amber-900",       desc: "Oversight + read-everywhere" },
  scope_user:  { label: "Scope User",    tint: "bg-cyan-100 text-cyan-800",         desc: "Owns master BOQ scope + commercial value" },
  csm:         { label: "CSM",           tint: "bg-orange-100 text-orange-800",     desc: "Client Success Manager · per-line PO management" },
  psm:         { label: "PSM",           tint: "bg-emerald-100 text-emerald-800",   desc: "Project Site Manager · measurements & install photos" },
  ism:         { label: "ISM",           tint: "bg-blue-100 text-blue-800",         desc: "Implementation Success Manager · signed DC uploads" },
  site_inspector: { label: "Site Inspector", tint: "bg-lime-100 text-lime-800",     desc: "On-site · uploads final installed photos · view-only BOQ + floorplan (ISM-assigned)" },
  designer:    { label: "Designer",      tint: "bg-purple-100 text-purple-800",     desc: "Uploads design concepts + declares materials" },
  prepress:    { label: "Pre-Press",     tint: "bg-fuchsia-100 text-fuchsia-800",   desc: "Reviews + uploads production files" },
  production:  { label: "Production",    tint: "bg-rose-100 text-rose-800",         desc: "QC documents · auto-triggers DC" },
  procurement_user: { label: "Procurement User", tint: "bg-amber-200 text-amber-900", desc: "Procures materials from approved designs" },
  pre_audit:   { label: "Pre-Audit",     tint: "bg-teal-100 text-teal-800",         desc: "Site pre-audit · measurements + photos (PSM-assigned)" },
  accounts:    { label: "Accounts",      tint: "bg-teal-100 text-teal-800",         desc: "Invoicing + PO downloads" },
  sales:       { label: "Sales",         tint: "bg-indigo-100 text-indigo-800",     desc: "PO follow-up · chases POs, sees commercials" },
  client:      { label: "Client",        tint: "bg-yellow-100 text-yellow-900",     desc: "Portal-only · design approvals" },
};
const ROLE_ORDER = [
  "admin", "management", "scope_user", "csm", "psm", "ism", "site_inspector",
  "designer", "prepress", "production", "procurement_user", "pre_audit", "accounts", "sales", "client",
];

export default function Users() {
  const { user: me } = useAuth();
  const isAdmin = me?.role === "admin";
  const [items, setItems] = useState([]);
  const [clients, setClients] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [newOpen, setNewOpen] = useState(false);
  const [editing, setEditing] = useState(null);   // user object
  const [wipeOpen, setWipeOpen] = useState(false);
  const [wipeConfirm, setWipeConfirm] = useState("");

  function load() {
    api.get("/users").then((r) => setItems(r.data));
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/user-types").then((r) => setUserTypes(r.data)).catch(() => {});
  }
  useEffect(load, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((u) => {
      if (roleFilter && u.role !== roleFilter) return false;
      if (!q) return true;
      return (
        (u.name || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q) ||
        (u.phone || "").includes(q)
      );
    });
  }, [items, search, roleFilter]);

  const groupCounts = useMemo(() => {
    const out = {};
    for (const u of items) out[u.role] = (out[u.role] || 0) + 1;
    return out;
  }, [items]);

  async function wipe() {
    if (wipeConfirm !== "WIPE ALL DATA") {
      toast.error("Type WIPE ALL DATA exactly to confirm");
      return;
    }
    try {
      const { data } = await api.post("/admin/wipe-data");
      toast.success(`Wiped ${data.dropped.length} collection(s)`);
      setWipeOpen(false); setWipeConfirm("");
      setTimeout(() => { window.location.href = "/"; }, 1200);
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail)); }
  }

  return (
    <div className="p-6 lg:p-10" data-testid="users-page">
      <PageHeader kicker="Admin" title="Team & Access">
        <Btn onClick={() => setNewOpen(true)} data-testid="add-user-btn">
          <Plus className="w-4 h-4" /> New User
        </Btn>
        {isAdmin && (
          <Btn variant="secondary" onClick={() => setWipeOpen(true)}
               data-testid="wipe-data-btn"
               className="border-rose-300 text-rose-700 hover:bg-rose-50">
            <AlertTriangle className="w-4 h-4" /> Wipe All Test Data
          </Btn>
        )}
      </PageHeader>

      {/* Role-count chips */}
      <div className="flex flex-wrap gap-2 mb-5" data-testid="role-chips">
        <button
          onClick={() => setRoleFilter("")}
          data-testid="role-chip-all"
          className={`px-3 h-8 rounded-full text-xs font-mono uppercase tracking-wider border ${
            !roleFilter
              ? "bg-zinc-900 text-white border-zinc-900"
              : "bg-white text-zinc-700 border-zinc-200 hover:border-zinc-400"
          }`}>
          All <span className="opacity-60 ml-1">{items.length}</span>
        </button>
        {ROLE_ORDER.filter((r) => groupCounts[r]).map((r) => {
          const m = ROLE_META[r] || { label: r, tint: "bg-zinc-100 text-zinc-700" };
          return (
            <button
              key={r}
              onClick={() => setRoleFilter(r === roleFilter ? "" : r)}
              data-testid={`role-chip-${r}`}
              className={`px-3 h-8 rounded-full text-xs font-mono uppercase tracking-wider border transition ${
                roleFilter === r
                  ? "ring-2 ring-orange-500 ring-offset-1 " + m.tint + " border-transparent"
                  : m.tint + " border-transparent opacity-80 hover:opacity-100"
              }`}>
              {m.label} <span className="opacity-70 ml-1">{groupCounts[r]}</span>
            </button>
          );
        })}
      </div>

      {/* Search */}
      <div className="card-flat p-3 flex items-center gap-3 mb-4">
        <Search className="w-4 h-4 text-zinc-400" />
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search name, email, phone…"
          data-testid="users-search"
          className="flex-1 outline-none text-sm bg-transparent" />
        {search && (
          <button onClick={() => setSearch("")}
                  className="text-xs text-zinc-400 hover:text-zinc-700">clear</button>
        )}
      </div>

      {/* Table */}
      <div className="card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left p-3">User</th>
              <th className="text-left p-3">Role</th>
              <th className="text-left p-3 hidden md:table-cell">Phone</th>
              <th className="text-left p-3 hidden lg:table-cell">Joined</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={5} className="p-10 text-center text-zinc-400 italic">
                  No users match the current filter.
                </td>
              </tr>
            ) : filtered.map((u) => {
              const m = ROLE_META[u.role] || { label: u.role.toUpperCase(), tint: "bg-zinc-100 text-zinc-700" };
              const isSelf = u.id === me?.id;
              return (
                <tr key={u.id} className="border-t border-zinc-100 hover:bg-zinc-50/50"
                    data-testid={`user-row-${u.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-100 to-amber-200 text-orange-800 flex items-center justify-center font-display font-bold text-sm flex-shrink-0">
                        {(u.name || "?")[0]?.toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="font-medium text-zinc-900 truncate flex items-center gap-1.5">
                          {u.name}
                          {isSelf && (
                            <span className="text-[9px] font-mono uppercase tracking-widest text-orange-700 bg-orange-50 px-1.5 py-0.5 rounded">
                              you
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] font-mono text-zinc-500 truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3">
                    <span className={`inline-flex items-center px-2.5 h-6 rounded-full text-[10px] font-mono uppercase tracking-widest ${m.tint}`}>
                      {m.label}
                    </span>
                    {u.user_type_name && (
                      <div className="text-[10px] text-zinc-500 mt-1 flex items-center gap-1" data-testid={`user-type-tag-${u.id}`}>
                        <ShieldCheck className="w-3 h-3" /> {u.user_type_name}
                      </div>
                    )}
                  </td>
                  <td className="p-3 font-mono text-xs text-zinc-600 hidden md:table-cell">
                    {u.phone || <span className="text-zinc-300">—</span>}
                  </td>
                  <td className="p-3 font-mono text-[11px] text-zinc-500 hidden lg:table-cell">
                    {fmtIST(u.created_at) || (u.created_at || "").slice(0, 10)}
                  </td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      <button onClick={() => setEditing(u)}
                              data-testid={`edit-user-${u.id}`}
                              title="Edit role / reset password"
                              className="inline-flex items-center gap-1 px-2.5 h-8 rounded-full bg-orange-600 text-white text-[11px] font-medium hover:bg-orange-700">
                        <Pencil className="w-3.5 h-3.5" /> Edit
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {newOpen && (
        <NewUserDialog
          clients={clients}
          userTypes={userTypes}
          onClose={() => setNewOpen(false)}
          onSaved={() => { setNewOpen(false); load(); }} />
      )}

      {editing && (
        <EditUserDialog
          user={editing}
          isSelf={editing.id === me?.id}
          clients={clients}
          userTypes={userTypes}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }} />
      )}

      {wipeOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white max-w-lg w-full p-6 space-y-4 border-2 border-rose-300 rounded-2xl">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-7 h-7 text-rose-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-display font-bold text-xl text-rose-700">Danger Zone — Wipe All Data</h3>
                <p className="text-sm text-zinc-600 mt-1">This will permanently delete <span className="font-semibold">all projects, BOQs, designs, challans, audit logs, and notifications</span>. Users + master data (clients, categories) are kept.</p>
              </div>
            </div>
            <Field label="Type WIPE ALL DATA to confirm">
              <Input value={wipeConfirm} onChange={(e) => setWipeConfirm(e.target.value)}
                     placeholder="WIPE ALL DATA" data-testid="wipe-confirm-input"
                     className="font-mono uppercase tracking-widest" />
            </Field>
            <div className="flex gap-2">
              <Btn onClick={wipe} className="bg-rose-700 hover:bg-rose-800" data-testid="wipe-confirm-btn">Wipe Data</Btn>
              <Btn variant="secondary" onClick={() => { setWipeOpen(false); setWipeConfirm(""); }}>Cancel</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ───────────────────────── New User dialog ───────────────────────── */
function NewUserDialog({ clients, userTypes = [], onClose, onSaved }) {
  // `roleSel` is either a base role string or "type:<id>" for a custom type.
  const [roleSel, setRoleSel] = useState("designer");
  const [form, setForm] = useState({
    email: "", name: "", password: "", phone: "", client_id: "",
  });
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);

  const isClient = roleSel === "client";

  async function save(e) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      const payload = {
        email: form.email, name: form.name, password: form.password,
        phone: form.phone, client_id: form.client_id,
      };
      if (roleSel.startsWith("type:")) payload.user_type_id = roleSel.slice(5);
      else payload.role = roleSel;
      await api.post("/users", payload);
      toast.success(`User created — ${form.email}`);
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <form onSubmit={save} data-testid="new-user-form"
            className="bg-white max-w-md w-full max-h-[90vh] flex flex-col rounded-2xl border border-zinc-200 overflow-hidden">
        <div className="flex-shrink-0 flex items-center justify-between bg-zinc-50 border-b border-zinc-200 px-5 py-3">
          <h3 className="font-display font-bold text-lg">New user</h3>
          <button type="button" onClick={onClose} className="text-zinc-400 hover:text-zinc-700">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          <Field label="Name" required>
            <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                   required data-testid="user-name-input" />
          </Field>
          <Field label="Email" required>
            <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.toLowerCase() })}
                   required data-testid="user-email-input" />
          </Field>
          <Field label="Password" required>
            <div className="relative">
              <Input type={showPwd ? "text" : "password"} value={form.password}
                     onChange={(e) => setForm({ ...form, password: e.target.value })}
                     required data-testid="user-password-input"
                     className="pr-24" />
              <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                        className="p-1 text-zinc-500 hover:text-zinc-800"
                        title={showPwd ? "Hide" : "Show"}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
                <button type="button" onClick={() => setForm({ ...form, password: genPwd() })}
                        className="text-[10px] font-mono uppercase tracking-widest text-orange-700 hover:bg-orange-50 px-2 py-1 rounded"
                        data-testid="user-password-gen">
                  Generate
                </button>
              </div>
            </div>
          </Field>
          <Field label="Phone (optional)">
            <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          </Field>
          <Field label="Role" required>
            <RolePicker value={roleSel} onChange={setRoleSel} testid="user-role" userTypes={userTypes} />
          </Field>
          {isClient && (
            <Field label="Linked client" required>
              <select value={form.client_id}
                      onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                      required
                      className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm"
                      data-testid="user-client-select">
                <option value="">— pick client —</option>
                {clients.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </Field>
          )}
        </div>
        <div className="flex-shrink-0 flex gap-2 px-5 py-3 bg-zinc-50 border-t border-zinc-200">
          <Btn type="submit" disabled={busy} data-testid="save-user">
            {busy ? "Creating…" : "Create user"}
          </Btn>
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
        </div>
      </form>
    </div>
  );
}

/* ──────────────────────── Edit User slide-over ──────────────────────── */
function EditUserDialog({ user, isSelf, clients, userTypes = [], onClose, onSaved, onDeleted }) {
  const [name, setName] = useState(user.name || "");
  const [phone, setPhone] = useState(user.phone || "");
  // roleSel is a base role string OR "type:<id>"
  const initialRoleSel = user.user_type_id ? `type:${user.user_type_id}` : user.role;
  const [roleSel, setRoleSel] = useState(initialRoleSel);
  const [clientId, setClientId] = useState(user.client_id || "");
  const [newPwd, setNewPwd] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [busy, setBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const isClient = roleSel === "client";
  const changed =
    name !== (user.name || "") ||
    phone !== (user.phone || "") ||
    roleSel !== initialRoleSel ||
    clientId !== (user.client_id || "") ||
    !!newPwd;

  async function save() {
    if (!changed || busy) return;
    setBusy(true);
    try {
      const payload = {};
      if (name !== user.name) payload.name = name;
      if (phone !== (user.phone || "")) payload.phone = phone;
      if (roleSel !== initialRoleSel) {
        if (roleSel.startsWith("type:")) payload.user_type_id = roleSel.slice(5);
        else { payload.role = roleSel; payload.user_type_id = ""; }
      }
      if (clientId !== (user.client_id || "")) payload.client_id = clientId;
      if (newPwd) payload.password = newPwd;
      const { data } = await api.patch(`/users/${user.id}`, payload);
      const bits = [];
      if ("name" in payload) bits.push("name");
      if ("phone" in payload) bits.push("phone");
      if ("user_type_id" in payload || "role" in payload) bits.push(`role → ${data.user_type_name || data.role}`);
      if ("password" in payload) bits.push("password reset");
      toast.success(`Updated: ${bits.join(" · ")}`);
      onSaved();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  async function del() {
    if (busy) return;
    setBusy(true);
    try {
      await api.delete(`/users/${user.id}`);
      toast.success(`Deleted ${user.email}`);
      onDeleted();
    } catch (e) {
      toast.error(fmtErr(e.response?.data?.detail));
    } finally { setBusy(false); }
  }

  function copyTempPwd() {
    if (!newPwd) return;
    navigator.clipboard.writeText(newPwd).then(
      () => toast.success("Copied — paste it into the user's chat / email."),
      () => toast.error("Clipboard blocked — copy manually"),
    );
  }

  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex justify-end"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white w-full sm:max-w-md h-full flex flex-col border-l border-zinc-200 shadow-2xl"
           data-testid="edit-user-panel">
        <div className="flex-shrink-0 bg-white border-b border-zinc-200 px-5 py-3.5 flex items-center justify-between">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-orange-100 to-amber-200 text-orange-800 flex items-center justify-center font-display font-bold flex-shrink-0">
              {(user.name || "?")[0]?.toUpperCase()}
            </div>
            <div className="min-w-0">
              <h3 className="font-display font-bold text-base leading-tight truncate">{user.name}</h3>
              <div className="text-[11px] font-mono text-zinc-500 mt-0.5 truncate">{user.email}</div>
            </div>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700 flex-shrink-0 ml-2"
                  data-testid="edit-user-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-5">
          {/* Profile */}
          <section>
            <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
              Profile
            </h4>
            <div className="space-y-3">
              <Field label="Name">
                <Input value={name} onChange={(e) => setName(e.target.value)}
                       data-testid="edit-user-name" />
              </Field>
              <Field label="Phone">
                <Input value={phone} onChange={(e) => setPhone(e.target.value)}
                       data-testid="edit-user-phone" />
              </Field>
            </div>
          </section>

          {/* Role */}
          <section>
            <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Role &amp; access
            </h4>
            <RolePicker value={roleSel} onChange={setRoleSel} testid="edit-user-role"
                        disabled={isSelf} userTypes={userTypes} />
            {isSelf && (
              <p className="text-[11px] text-amber-700 mt-2 italic">
                You can't change your own role. Ask another admin if you need this.
              </p>
            )}
            {isClient && (
              <Field label="Linked client" required>
                <select value={clientId} onChange={(e) => setClientId(e.target.value)}
                        className="w-full h-11 px-3 border border-zinc-300 rounded-lg text-sm mt-2"
                        data-testid="edit-user-client">
                  <option value="">— pick client —</option>
                  {clients.map((c) => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
              </Field>
            )}
          </section>

          {/* Password */}
          <section>
            <h4 className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3 flex items-center gap-1.5">
              <KeyRound className="w-3.5 h-3.5" /> Reset password
            </h4>
            <p className="text-xs text-zinc-500 mb-2 leading-relaxed">
              Leave blank to keep the current password. Generate a strong one and
              copy it to share with the user securely.
            </p>
            <div className="relative">
              <Input type={showPwd ? "text" : "password"}
                     value={newPwd}
                     onChange={(e) => setNewPwd(e.target.value)}
                     placeholder="Leave blank to keep current"
                     data-testid="edit-user-newpwd"
                     className="pr-32" />
              <div className="absolute inset-y-0 right-2 flex items-center gap-1">
                <button type="button" onClick={() => setShowPwd((v) => !v)}
                        className="p-1 text-zinc-500 hover:text-zinc-800"
                        title={showPwd ? "Hide" : "Show"}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
                </button>
                <button type="button" onClick={() => setNewPwd(genPwd())}
                        className="text-[10px] font-mono uppercase tracking-widest text-orange-700 hover:bg-orange-50 px-2 py-1 rounded"
                        data-testid="edit-user-genpwd">
                  Generate
                </button>
                {newPwd && (
                  <button type="button" onClick={copyTempPwd}
                          className="p-1 text-zinc-500 hover:text-zinc-800"
                          title="Copy">
                    <Copy className="w-4 h-4" />
                  </button>
                )}
              </div>
            </div>
            {newPwd && (
              <div className="mt-2 text-[11px] font-mono text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2">
                Will be saved on click of <b>Save changes</b>. Share it securely with the user.
              </div>
            )}
          </section>

          {/* Danger */}
          {!isSelf && (
            <section className="pt-2 border-t border-zinc-100">
              <h4 className="text-[10px] font-mono uppercase tracking-widest text-rose-600 mb-2 flex items-center gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5" /> Danger zone
              </h4>
              {!confirmDelete ? (
                <button onClick={() => setConfirmDelete(true)}
                        data-testid="delete-user-btn"
                        className="inline-flex items-center gap-1.5 px-3 h-9 rounded-full border border-rose-300 text-rose-700 text-xs font-medium hover:bg-rose-50">
                  <Trash2 className="w-3.5 h-3.5" /> Delete this user
                </button>
              ) : (
                <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 space-y-2">
                  <p className="text-xs text-rose-800">
                    Deleting <b>{user.email}</b> is permanent. They will no longer be able to log in. Any data
                    they created stays in the system.
                  </p>
                  <div className="flex gap-2">
                    <button onClick={del} disabled={busy}
                            data-testid="delete-user-confirm"
                            className="px-3 h-8 rounded-full bg-rose-700 text-white text-xs font-medium hover:bg-rose-800 disabled:opacity-60">
                      {busy ? "Deleting…" : "Yes, delete"}
                    </button>
                    <button onClick={() => setConfirmDelete(false)}
                            className="px-3 h-8 rounded-full border border-zinc-300 text-zinc-700 text-xs font-medium hover:bg-white">
                      Cancel
                    </button>
                  </div>
                </div>
              )}
            </section>
          )}
        </div>

        <div className="flex-shrink-0 bg-white border-t border-zinc-200 px-5 py-3 flex gap-2">
          <button onClick={save} disabled={!changed || busy}
                  data-testid="edit-user-save"
                  className="flex-1 h-11 rounded-xl bg-orange-600 text-white font-medium hover:bg-orange-700 disabled:opacity-50">
            {busy ? "Saving…" : changed ? "Save changes" : "No changes"}
          </button>
          <button onClick={onClose}
                  className="h-11 px-4 rounded-xl border border-zinc-300 text-zinc-700 font-medium hover:bg-zinc-50">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────── Role picker grid ──────────────────────── */
function RolePicker({ value, onChange, testid, disabled, userTypes = [] }) {
  return (
    <div data-testid={testid} className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        {ROLE_ORDER.map((r) => {
          const m = ROLE_META[r];
          const active = value === r;
          return (
            <button
              key={r}
              type="button"
              onClick={() => !disabled && onChange(r)}
              data-testid={`${testid}-opt-${r}`}
              disabled={disabled}
              className={`text-left rounded-xl border p-2.5 transition disabled:opacity-60 disabled:cursor-not-allowed ${
                active
                  ? "ring-2 ring-orange-500 border-orange-300 bg-orange-50/40"
                  : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
              }`}>
              <div className="flex items-center justify-between mb-1">
                <span className={`text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded ${m.tint}`}>
                  {m.label}
                </span>
                {active && <span className="w-1.5 h-1.5 rounded-full bg-orange-500" />}
              </div>
              <div className="text-[10px] text-zinc-500 leading-tight line-clamp-2">{m.desc}</div>
            </button>
          );
        })}
      </div>
      {userTypes.length > 0 && (
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-2 flex items-center gap-1.5">
            <ShieldCheck className="w-3 h-3" /> Custom user types
          </div>
          <div className="grid grid-cols-2 gap-2">
            {userTypes.map((t) => {
              const key = `type:${t.id}`;
              const active = value === key;
              return (
                <button
                  key={t.id}
                  type="button"
                  onClick={() => !disabled && onChange(key)}
                  data-testid={`${testid}-type-${t.id}`}
                  disabled={disabled}
                  className={`text-left rounded-xl border p-2.5 transition disabled:opacity-60 disabled:cursor-not-allowed ${
                    active
                      ? "ring-2 ring-orange-500 border-orange-300 bg-orange-50/40"
                      : "border-zinc-200 hover:border-zinc-300 hover:bg-zinc-50"
                  }`}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-800 truncate">
                      {t.name}
                    </span>
                    {active && <span className="w-1.5 h-1.5 rounded-full bg-orange-500 flex-shrink-0" />}
                  </div>
                  <div className="text-[10px] text-zinc-500 leading-tight">
                    base: {(ROLE_META[t.base_role]?.label) || t.base_role} · {(t.allowed_pages || []).length} pages
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

/* Strong password generator — readable + memorable */
function genPwd() {
  const a = "abcdefghjkmnpqrstuvwxyz";          // skip i/l/o for legibility
  const A = "ABCDEFGHJKLMNPQRSTUVWXYZ";          // skip I/O
  const d = "23456789";                          // skip 0/1
  const s = "!@#$%&*?";
  const pool = a + A + d + s;
  let pwd = a[Math.floor(Math.random() * a.length)] +
            A[Math.floor(Math.random() * A.length)] +
            d[Math.floor(Math.random() * d.length)] +
            s[Math.floor(Math.random() * s.length)];
  for (let i = 0; i < 9; i++) {
    pwd += pool[Math.floor(Math.random() * pool.length)];
  }
  return pwd.split("").sort(() => Math.random() - 0.5).join("");
}
